import { computed, onMounted, ref } from 'vue';
import { useUnitsStore } from 'stores/units-store';
import type { TUnitDataDictionaryEntry } from 'types';
import { useAnnualReturnsVisibility } from './useAnnualReturnsVisibility';

/**
 * Resolves the units the current user may manage annual returns for.
 *
 * The unit IDs are the units the user's permission bundle grants (`MANAGE_UNIT`)
 * narrowed to those live on the Portal, so a secretary of several units is
 * offered only the ones whose returns have moved off Adelphi. The descriptive
 * fields (name, number, region) are hydrated from the shared units data
 * dictionary so this composable owns no unit detail of its own. Returned units
 * are sorted by name for a stable selector order.
 */
export function useManageableUnits() {
  const unitsStore = useUnitsStore();

  /** Unit IDs the user can manage, as permitted by the ACL and live on the Portal. */
  const { visibleUnitIds: manageableUnitIds } = useAnnualReturnsVisibility();

  const isLoading = ref(false);

  /** Hydrated data-dictionary entries for those IDs, sorted by name. */
  const manageableUnits = computed<TUnitDataDictionaryEntry[]>(() => {
    const allowedIds = new Set(manageableUnitIds.value);
    return unitsStore.dataDictionary
      .filter((unit) => allowedIds.has(unit.id))
      .sort((unitA, unitB) => unitA.unit_name.localeCompare(unitB.unit_name));
  });

  const hasMultipleUnits = computed(() => manageableUnits.value.length > 1);

  /**
   * Ensure the units data dictionary is loaded so the IDs can be hydrated. The
   * dictionary is cached (localStorage + background refresh) by the units store,
   * so this is cheap on repeat calls.
   */
  async function ensureUnitsLoaded(): Promise<void> {
    if (unitsStore.dataDictionary.length > 0) return;
    isLoading.value = true;
    try {
      await unitsStore.getDataDictionary();
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(() => {
    void ensureUnitsLoaded();
  });

  return {
    isLoading,
    manageableUnitIds,
    manageableUnits,
    hasMultipleUnits,
    ensureUnitsLoaded,
  };
}
